import { Localized, useLocalization } from "@fluent/react"
import { unwrapResult } from "@reduxjs/toolkit"
import { ArrowDropDown, ArrowRight, Close, FolderOpen, Search, Settings, UnfoldLess, UnfoldMore } from "@mui/icons-material"
import { Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, InputAdornment, Stack, TextField, Typography } from "@mui/material"
import { SimpleTreeView } from "@mui/x-tree-view/SimpleTreeView"
import { TreeItem } from "@mui/x-tree-view/TreeItem"
import { useEffect, useMemo, useState } from "react"
import { CancelButton } from "../../../app/components/CancelButton"
import { useAppDispatch } from "../../../app/hooks"
import {
    loadCompanyStructuresDefinitions,
    loadStructureTree,
    StructureNodeTreeDto,
} from "../structureSlice"

interface CustomDeviceStructureNodeSelectProps {
    value: string | null
    onChange: (node: StructureNodeTreeDto) => void
    disabled?: boolean
}

const findNode = (nodes: StructureNodeTreeDto[], id: string): StructureNodeTreeDto | undefined => {
    for (const node of nodes) {
        if (node.id === id) return node
        const child = findNode(node.children, id)
        if (child) return child
    }
}

const expandableNodeIds = (nodes: StructureNodeTreeDto[]): string[] =>
    nodes.flatMap(node => node.children.length > 0
        ? [node.id, ...expandableNodeIds(node.children)]
        : [])

const filterNodes = (nodes: StructureNodeTreeDto[], query: string): StructureNodeTreeDto[] =>
    nodes.flatMap(node => {
        if (`${node.code} ${node.name}`.toLocaleLowerCase().includes(query)) return [node]
        const children = filterNodes(node.children, query)
        return children.length > 0 ? [{ ...node, children }] : []
    })

const NodeItem = ({ node }: { node: StructureNodeTreeDto }) => (
    <TreeItem
        itemId={node.id}
        label={
            <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 0.5 }}>
                {node.children.length > 0 ? <FolderOpen fontSize="small" /> : <Settings fontSize="small" />}
                <Typography variant="body2">
                    {node.code ? `${node.code} - ` : ""}{node.name}
                </Typography>
            </Stack>
        }
    >
        {node.children.map(child => <NodeItem key={child.id} node={child} />)}
    </TreeItem>
)

export const CustomDeviceStructureNodeSelect = ({
    value,
    onChange,
    disabled = false,
}: CustomDeviceStructureNodeSelectProps) => {
    const dispatch = useAppDispatch()
    const { l10n } = useLocalization()
    const [nodes, setNodes] = useState<StructureNodeTreeDto[]>([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<"definitions" | "tree" | "missing" | null>(null)
    const [open, setOpen] = useState(false)
    const [draftId, setDraftId] = useState<string | null>(null)
    const [expanded, setExpanded] = useState<string[]>([])
    const [search, setSearch] = useState("")
    const filteredNodes = useMemo(
        () => search.trim() ? filterNodes(nodes, search.trim().toLocaleLowerCase()) : nodes,
        [nodes, search],
    )

    useEffect(() => {
        let cancelled = false
        setLoading(true)
        setError(null)
        setNodes([])

        const load = async () => {
            let definitions
            try {
                definitions = await dispatch(loadCompanyStructuresDefinitions({ onlyActive: true })).then(unwrapResult)
            } catch (error) {
                console.error(error)
                if (!cancelled) setError("definitions")
                return
            }

            if (cancelled) return
            const definition = definitions.find(item => item.type === "CUSTOM_DEVICE_STRUCTURE")
            if (!definition) {
                setError("missing")
                return
            }

            try {
                const tree = await dispatch(loadStructureTree({
                    structureDefinitionId: definition.id,
                    onlyActive: true,
                })).then(unwrapResult)
                if (!cancelled) {
                    setNodes(tree.roots)
                    setExpanded(expandableNodeIds(tree.roots))
                }
            } catch (error) {
                console.error(error)
                if (!cancelled) setError("tree")
            }
        }

        load().finally(() => {
            if (!cancelled) setLoading(false)
        })

        return () => { cancelled = true }
    }, [dispatch])

    const handleOpen = () => {
        setDraftId(value)
        setSearch("")
        setExpanded(expandableNodeIds(nodes))
        setOpen(true)
    }

    const handleSearchChange = (query: string) => {
        setSearch(query)
        setExpanded(expandableNodeIds(query.trim() ? filterNodes(nodes, query.trim().toLocaleLowerCase()) : nodes))
    }

    const handleConfirm = () => {
        const node = draftId ? findNode(nodes, draftId) : undefined
        if (!node) return
        onChange(node)
        setOpen(false)
    }

    const selectedNode = value ? findNode(nodes, value) : undefined
    const selectedDraftNode = draftId ? findNode(filteredNodes, draftId) : undefined
    const errorMessage = error === "missing"
        ? l10n.getString("structures-device-select-not-configured", null, "_Własna struktura urządzeń nie jest skonfigurowana.")
        : error === "definitions"
            ? l10n.getString("structures-device-select-definitions-error", null, "_Nie udało się załadować definicji struktur.")
            : l10n.getString("structures-tree-load-error", null, "_Nie udało się załadować drzewa struktury.")

    return (
        <Box>
            <TextField
                label={<Localized id="structures-device-select-field-label">_Nazwa własnej struktury</Localized>}
                value={selectedNode ? `${selectedNode.code ? `${selectedNode.code} - ` : ""}${selectedNode.name}` : ""}
                onClick={handleOpen}
                disabled={disabled}
                fullWidth
                InputLabelProps={{ shrink: true }}
                InputProps={{
                    readOnly: true,
                    endAdornment: (
                        <InputAdornment position="end">
                            <IconButton
                                onClick={handleOpen}
                                disabled={disabled}
                                aria-label={l10n.getString("structures-device-select-field-label", null, "_Nazwa własnej struktury")}
                            >
                                <ArrowDropDown />
                            </IconButton>
                        </InputAdornment>
                    ),
                }}
            />
            <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="md">
                <DialogTitle sx={{ bgcolor: "grey.200", color: "text.primary" }}>
                    <Localized id="structures-device-select-label">_Wybierz element struktury urządzeń</Localized>
                </DialogTitle>
                <DialogContent dividers sx={{ height: "80vh", display: "flex", flexDirection: "column", p: 0 }}>
                    {loading ? (
                        <CircularProgress size={24} sx={{ m: 2 }} />
                    ) : error ? (
                        <Alert severity="error" sx={{ m: 2 }}>{errorMessage}</Alert>
                    ) : nodes.length === 0 ? (
                        <Alert severity="info" sx={{ m: 2 }}>
                            <Localized id="structures-tree-empty">_Brak elementów w drzewie.</Localized>
                        </Alert>
                    ) : (
                        <>
                            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ p: 1, borderBottom: 1, borderColor: "divider" }}>
                                <Button size="small" variant="outlined" startIcon={<UnfoldMore />} onClick={() => setExpanded(expandableNodeIds(filteredNodes))}>
                                    <Localized id="structure-expand-all">_Rozwiń wszystko</Localized>
                                </Button>
                                <Button size="small" variant="outlined" startIcon={<UnfoldLess />} onClick={() => setExpanded([])}>
                                    <Localized id="structure-collapse-all">_Zwiń wszystko</Localized>
                                </Button>
                                <TextField
                                    size="small"
                                    value={search}
                                    onChange={event => handleSearchChange(event.target.value)}
                                    placeholder={l10n.getString("structures-device-select-search", null, "_Szukaj po nazwie lub kodzie")}
                                    inputProps={{ "aria-label": l10n.getString("structures-device-select-search", null, "_Szukaj po nazwie lub kodzie") }}
                                    sx={{ flex: "1 1 200px" }}
                                    InputProps={{
                                        startAdornment: <InputAdornment position="start"><Search /></InputAdornment>,
                                        endAdornment: search && (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    size="small"
                                                    aria-label={l10n.getString("clear", null, "_Wyczyść")}
                                                    onClick={() => handleSearchChange("")}
                                                >
                                                    <Close fontSize="small" />
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Stack>
                            <Box sx={{ overflowY: "auto", flex: 1, p: 1 }}>
                                {filteredNodes.length === 0 ? (
                                    <Alert severity="info">
                                        <Localized id="structures-device-select-no-results">_Brak pasujących elementów.</Localized>
                                    </Alert>
                                ) : (
                                    <SimpleTreeView
                                        aria-label={l10n.getString("structures-device-select-label", null, "_Wybierz element struktury urządzeń")}
                                        slots={{ collapseIcon: ArrowDropDown, expandIcon: ArrowRight }}
                                        selectedItems={draftId ?? ""}
                                        onSelectedItemsChange={(_, id) => setDraftId(id)}
                                        expandedItems={expanded}
                                        onExpandedItemsChange={(_, ids) => setExpanded(ids)}
                                    >
                                        {filteredNodes.map(node => <NodeItem key={node.id} node={node} />)}
                                    </SimpleTreeView>
                                )}
                            </Box>
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button size="small" variant="contained" onClick={handleConfirm} disabled={!selectedDraftNode || loading || !!error}>
                        <Localized id="select">_Wybierz</Localized>
                    </Button>
                    <CancelButton onClick={() => setOpen(false)} loading={false} />
                </DialogActions>
            </Dialog>
        </Box>
    )
}
